/**
 * Display labels.
 *
 * The API sends a ready `section_label` ("الصف أول - أولى م (ص)") built
 * by the `section_labels` view, because `title` alone is NOT a
 * section's identity — the schema keys it on
 * (grade_id, batch_id, academic_year_id, title), and in the live data
 * "أولى م" names twelve different sections.
 *
 * These helpers exist so no screen ever composes a label itself. They
 * only read what the server sent, with a fallback for the one case
 * where the field is genuinely absent (an older response shape).
 */

/** A section's display name. Never build this string anywhere else. */
export function sectionLabel(section) {
  if (!section) return '—';
  if (section.section_label) return section.section_label;
  // Fallback: a response that predates the label view. Compose only
  // from fields that are certainly on the row, and never silently
  // return a bare title, which would be ambiguous.
  const grade = section.grade_title ? `الصف ${section.grade_title} - ` : '';
  const batch = section.batch_title ? ` (${section.batch_title})` : '';
  return `${grade}${section.title || section.section_title || '—'}${batch}`;
}

/** Sections as <Select> options, labelled unambiguously. */
export function sectionOptions(sections, { withCount = false } = {}) {
  return (sections || []).map((s) => ({
    value: s.id ?? s.section_id,
    label: withCount && s.student_count != null
      ? `${sectionLabel(s)} — ${s.student_count} طالب`
      : sectionLabel(s),
  }));
}

/** A person's name. One stored field; this only guards against blanks. */
export function personName(person, fallback = '—') {
  if (!person) return fallback;
  const name = (person.name || person.student_name || person.teacher_name || '').trim();
  return name || (person.id ? `#${person.id}` : fallback);
}

/** رقم التسجيل for display: digits-only numbers padded to 6 with a leading
 *  "#" (60000 → "#060000", imported 12 → "#000012"); anything else
 *  (old text ids such as "59/30") is shown as it is. */
export function formatRegNo(v) {
  if (v === null || v === undefined || String(v).trim() === '') return '';
  const s = String(v).trim();
  return /^\d+$/.test(s) ? `#${s.padStart(6, '0')}` : s;
}
